// Registro central de la sección Listados: por cada listado, su clave, título, ruta, ícono,
// permiso y columnas. Alimenta la barra de accesos entre listados (AccesosListados).
//
// OJO: el menú lateral (config/menuSections.js) y las rutas (App.js) NO se arman desde acá:
// tienen sus entradas escritas a mano. Un listado nuevo toca este archivo, su página fina, App.js
// y menuSections.js.
import { useState } from 'react';
import { getCobros } from '../../services/cobranzaService';
import { PAGO_TODO, esPagoTodo } from '../../components/Cobranzas/resumenCobro';
import { getTramitesParaLiquidar, getTramitesParaAprobar } from '../../services/tramiteService';
import { getCarpetasJauregui } from '../../services/jaureguiService';
import { listarPlanilla } from '../../services/planillaHistoricaService';
import { listarVolverAContactar } from '../../services/volverAContactarService';
import { formatearTipoTramite } from '../../constants/tiposTramiteTurno';
import { apellidoNombre } from '../../utils/nombrePersona';
import { rutaDetalleTramite } from '../../utils/rutasTramite';

/**
 * Adónde va el click de una fila de cobro: si ya tiene un trámite enganchado, directo a su
 * ficha (ahí vive la gestión completa, ver SeccionCobranzaTramite) — el modal queda solo para
 * los cobros sin carpeta todavía, que no tienen ficha de trámite a la que ir.
 */
export function irADetalleCobro(row, navigate, abrirModal) {
    const primero = Array.isArray(row.tramites) && row.tramites.length > 0 ? row.tramites[0] : null;
    if (primero?.idTramite) {
        navigate(rutaDetalleTramite({ idTramite: primero.idTramite, nombreTipoTramite: primero.tipoTramiteNombre }));
    } else {
        abrirModal(row.idCobro);
    }
}

export const formatoFecha = (valor) => {
    if (!valor) return '—';
    const fecha = new Date(`${valor}T00:00:00`);
    if (Number.isNaN(fecha.getTime())) return '—';
    return fecha.toLocaleDateString('es-AR');
};

export const formatoMoneda = (valor) => {
    if (valor === null || valor === undefined) return '—';
    const numero = Number(valor);
    if (Number.isNaN(numero)) return '—';
    return numero.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
};

// Primer trámite enganchado al cobro, con link directo a su ficha (si ya existe la carpeta).
export function ColumnaTramite({ tramites }) {
    const primero = Array.isArray(tramites) && tramites.length > 0 ? tramites[0] : null;
    if (!primero) return <span className="text-muted">—</span>;

    const extra = tramites.length > 1 ? ` (+${tramites.length - 1})` : '';
    if (!primero.idTramite) {
        return <span>{primero.tipoTramiteNombre || 'Sin tipo'}{extra}</span>;
    }
    const ruta = rutaDetalleTramite({ idTramite: primero.idTramite, nombreTipoTramite: primero.tipoTramiteNombre });
    return (
        <a
            href={ruta}
            onClick={(e) => e.stopPropagation()}
            style={{ color: '#1e40af', textDecoration: 'none', fontWeight: 500 }}
        >
            {primero.tipoTramiteNombre || 'Ver trámite'}{extra}
        </a>
    );
}

export function ColumnaCliente({ row }) {
    return (
        <div className="text-start">
            <div style={{ fontWeight: 500, color: '#374151' }}>
                {[row.apellido, row.nombre].filter(Boolean).join(', ') || 'N/A'}
            </div>
            {/* Copiable con un click, como en "A liquidar" y "Volver a contactar": se pega en ANSES.
                DatoCopiable frena el click para que copiar no abra la ficha. */}
            {row.cuil && (
                <div style={{ fontSize: '0.8rem', color: '#4b5563' }}>
                    <DatoCopiable valor={row.cuil} />
                </div>
            )}
        </div>
    );
}

const columnaCliente = { key: 'cliente', header: 'Cliente', className: 'text-start', render: (row) => <ColumnaCliente row={row} /> };
const columnaTramite = { key: 'tramite', header: 'Trámite', render: (row) => <ColumnaTramite tramites={row.tramites} /> };
const columnaTelefono = { key: 'telefono', header: 'Teléfono', render: (row) => row.telefono || '—' };
const columnaBanco = { key: 'banco', header: 'Banco', render: (row) => row.banco || '—' };
const columnaPacto = { key: 'pacto', header: 'Pacto', render: (row) => row.pactoDescripcion || '—' };
const columnaObservaciones = {
    key: 'observaciones', header: 'Observaciones',
    render: (row) => (
        <span title={row.observaciones || ''} style={{ display: 'inline-block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.observaciones || '—'}
        </span>
    ),
};
const columnaUltimaGestion = {
    key: 'ultimaGestion', header: 'Última gestión',
    render: (row) => row.ultimaGestionDetalle
        ? <span title={row.ultimaGestionDetalle}>{formatoFecha(row.ultimaGestionFecha)} — {row.ultimaGestionDetalle}</span>
        : <span className="text-muted">Sin gestión</span>,
};

/**
 * Celda de texto libre que en la planilla es un párrafo entero ("SE INICIO SEC. 02 DE JUB X INV
 * VA A SALIR DESFAVORABLE POR FALTA DE DDJJ..."). Se recorta a un ancho fijo y el texto completo
 * queda en el title: si se dejara entero, una sola fila empuja la tabla fuera de la pantalla.
 */
export function celdaTextoLargo(valor, ancho = 240) {
    if (!valor) return <span className="text-muted">—</span>;
    return (
        <span title={valor} style={{ display: 'inline-block', maxWidth: ancho, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'inherit' }}>
            {valor}
        </span>
    );
}

/**
 * Columna cuyo dato todavía NO existe en el sistema: la planilla lo lleva a mano y no hay campo
 * donde guardarlo. Se muestra igual, y vacía, para que al validar el listado con el estudio se
 * vea exactamente qué falta — en vez de esconder la columna y que el faltante aparezca recién
 * cuando alguien la busque. Ver ParaAprobarDto, que documenta cada uno.
 */
function celdaSinConectar() {
    return <span style={{ color: '#6b7280' }} title="Este dato todavía no se carga en el sistema">—</span>;
}

/**
 * Las tres claves del cliente en una sola columna, una debajo de la otra.
 *
 * Por qué así y no tres columnas: en la planilla son tres (CLAVE SS, AFIP, CLAVE CIDI) y es
 * justamente lo que empuja la tabla fuera de la pantalla. Acá ocupan un ancho fijo y se leen las
 * tres sin hacer nada — que es para lo que se usan: el cliente llama, alguien las dicta.
 *
 * Click en una la copia al portapapeles: el uso real es pegarla en el sitio de ANSES/AFIP.
 */
/**
 * Un dato que se dicta o se pega en otro lado: click y queda en el portapapeles.
 *
 * Son los que en el escritorio se leen en voz alta o se pegan en el sitio de ANSES/AFIP — el CUIL
 * y las tres claves. Tienen que comportarse igual los cuatro, por eso el gesto vive en un solo
 * componente y no repetido en cada celda.
 *
 * El stopPropagation es imprescindible: la fila entera navega a la ficha del trámite, y sin él
 * copiar el CUIL te saca del listado.
 */
export function DatoCopiable({ valor, estilo, className }) {
    const [copiado, setCopiado] = useState(false);

    const copiar = (e) => {
        e.stopPropagation();
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(valor).then(() => {
            setCopiado(true);
            setTimeout(() => setCopiado(false), 1200);
        }).catch(() => { /* sin portapapeles (http, permisos): el dato igual se ve y se puede seleccionar */ });
    };

    return (
        <span
            className={className}
            title={copiado ? 'Copiado' : `${valor} — click para copiar`}
            onClick={copiar}
            style={{ cursor: 'copy', fontSize: 'inherit', ...estilo, ...(copiado ? { color: '#047857' } : null) }}
        >
            {valor}
            {copiado && <i className="bi bi-check2 ms-1" />}
        </span>
    );
}

export function CeldaClaves({ row }) {
    const claves = [
        { sigla: 'SS', valor: row.claveSs },
        { sigla: 'AFIP', valor: row.claveFiscal },
        { sigla: 'CIDI', valor: row.claveCidi },
    ];
    // Mi Argentina va aparte y solo si vino: a diferencia de las otras tres NO es un dato del
    // cliente (ClaveEntity no lo tiene), vive en tramite_jubilacion. Así la celda no muestra un
    // renglón vacío permanente en los listados donde ese dato no existe.
    if (row.miArgentina) {
        claves.push({ sigla: 'MiArg', valor: row.miArgentina });
    }
    if (claves.every((c) => !c.valor)) {
        return <span style={{ color: '#6b7280', fontSize: 'inherit' }}>—</span>;
    }
    return (
        <div className="text-start" style={{ fontSize: '0.75rem', lineHeight: 1.5, width: 128 }}>
            {claves.map(({ sigla, valor }) => (
                <div key={sigla} className="d-flex gap-2" style={{ fontSize: 'inherit' }}>
                    <span style={{ color: '#4b5563', fontWeight: 600, width: 34, flexShrink: 0, fontSize: 'inherit' }}>{sigla}</span>
                    {valor ? (
                        <DatoCopiable
                            valor={valor}
                            estilo={{
                                color: '#374151', overflow: 'hidden',
                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                        />
                    ) : (
                        <span style={{ color: '#6b7280', fontSize: 'inherit' }}>—</span>
                    )}
                </div>
            ))}
        </div>
    );
}

/**
 * Cuándo se liquida. Sin fecha es una carpeta a la que nadie le asignó mes (el bloque "Sin fecha
 * asignada"): se marca en gris para que se note que falta decidirlo.
 */
function CeldaFechaLiquidar({ row, onAsignarMes }) {
    if (!row.fechaInicioEstimada) {
        return (
            <div className="d-flex flex-column align-items-center gap-1">
                <span style={{ color: '#4b5563', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>Sin fecha</span>
                {onAsignarMes && <AsignarMes row={row} onAsignarMes={onAsignarMes} />}
                <ProximasFechas row={row} />
            </div>
        );
    }
    return (
        <div className="d-flex flex-column align-items-center gap-1">
            <span style={{ color: row.vencido ? '#b91c1c' : '#374151', fontWeight: row.vencido ? 600 : 400, whiteSpace: 'nowrap' }}>
                {row.vencido && <i className="bi bi-exclamation-triangle-fill me-1" />}
                {formatoFecha(row.fechaInicioEstimada)}
            </span>
            <ProximasFechas row={row} />
        </div>
    );
}

/** "Después: 07/2032 · TRANSF DE PUAM A JUB": las otras cosas que la planilla anotó para la carpeta. */
function ProximasFechas({ row }) {
    const proximas = row.proximasFechas || [];
    if (proximas.length === 0) return null;
    return (
        <div style={{ fontSize: '0.75rem', color: '#4b5563', maxWidth: 150, lineHeight: 1.25 }}>
            {proximas.map((p) => (
                <div key={p.fecha} title={p.descripcion || ''}>
                    Después: <strong>{formatoMesAnio(p.fecha)}</strong>
                    {p.descripcion && ` · ${p.descripcion.length > 40 ? `${p.descripcion.slice(0, 40)}…` : p.descripcion}`}
                </div>
            ))}
        </div>
    );
}

const formatoMesAnio = (iso) => {
    const [anio, mes] = String(iso || '').split('-');
    return anio && mes ? `${mes}/${anio}` : '';
};

/**
 * Asignar el mes ahí mismo, sin abrir nada: es para las carpetas que vinieron de la planilla sin
 * una fecha vigente, que se revisan de a muchas seguidas. El clic en la celda no abre la ficha.
 */
function AsignarMes({ row, onAsignarMes }) {
    const [mes, setMes] = useState('');
    const [guardando, setGuardando] = useState(false);
    const anio = Number(mes.slice(0, 4));
    const valido = /^\d{4}-\d{2}$/.test(mes) && anio >= 2000 && anio <= 2100;

    const asignar = async (e) => {
        e.stopPropagation();
        if (!valido) return;
        setGuardando(true);
        try {
            await onAsignarMes(row, mes);
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div className="d-flex gap-1" onClick={(e) => e.stopPropagation()}>
            <input
                type="month" className="form-control form-control-sm" style={{ width: 120 }}
                aria-label="Mes de liquidar" value={mes} onChange={(e) => setMes(e.target.value)}
            />
            <button type="button" className="btn btn-sm btn-primary" disabled={!valido || guardando}
                    onClick={asignar} title="Asignar este mes">
                {guardando ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-check-lg" />}
            </button>
        </div>
    );
}

/**
 * La columna "ESTADO DEL CONTROL" de la planilla, más el botón que abre el modal.
 *
 * El botón vive acá y no en una columna de acciones propia para no sumar una novena columna:
 * controlar la carpeta es exactamente lo que esta celda describe, así que el gesto va donde está
 * el dato.
 */
function CeldaControl({ row, onGestionar }) {
    return (
        <div className="text-start d-flex flex-column gap-1" style={{ maxWidth: 170 }}>
            {row.fechaControlLiquidar ? (
                <>
                    <span style={{ color: '#047857', fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        <i className="bi bi-check-circle-fill me-1" />
                        Controlada {formatoFecha(row.fechaControlLiquidar)}
                    </span>
                    {row.notaControlLiquidar && (
                        <span
                            title={`${row.notaControlLiquidar}${row.usuarioControlLiquidar ? ` — ${row.usuarioControlLiquidar}` : ''}`}
                            style={{
                                fontSize: '0.75rem', color: '#6b7280', overflow: 'hidden',
                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                        >
                            {row.notaControlLiquidar}
                        </span>
                    )}
                </>
            ) : (
                <span style={{ color: '#4b5563', fontSize: '0.8rem' }}>Sin controlar</span>
            )}
            {/* Envuelto en un span: si el botón queda hijo directo del div, la regla global
                "td:has(> div > .btn)" deja la celda sin cortar líneas y ensancha la tabla. */}
            {onGestionar && (
                <span className="align-self-start"><button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    style={{ padding: '1px 8px', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                    onClick={(e) => { e.stopPropagation(); onGestionar(row); }}
                >
                    <i className="bi bi-clipboard-check me-1" />Gestionar
                </button></span>
            )}
        </div>
    );
}

/**
 * Las columnas del listado "A liquidar", en el orden de la solapa LIQUIDAR de la planilla.
 *
 * Es una función y no una constante porque la última columna necesita el callback que abre el
 * modal de control, y ese solo lo tiene la página. Sin callback las columnas se dibujan igual,
 * sin el botón — así el registro de LISTADOS sigue siendo declarativo.
 *
 * El teléfono queda afuera a propósito: en la planilla es la columna más ancha
 * ("3512360390//3517470833 (HIJA, FABIANA)") y es la que rompe el ancho de la tabla. Está a un
 * click, en la ficha del cliente.
 */
export function columnasALiquidar(onGestionar = null, acciones = {}) {
    const { onAsignarMes = null, onVerPlanilla = null, planillasAbiertas = null } = acciones;
    return [
        {
            key: 'cliente', header: 'Cliente', className: 'text-start',
            render: (row) => (
                <div className="text-start">
                    <div style={{ fontWeight: 500, color: '#374151' }}>
                        {apellidoNombre(row.apellidoCliente, row.nombreCliente) || 'N/A'}
                    </div>
                    {/* Sin la clase .btn a propósito: la regla global "td:has(> div > .btn)" deja la
                        celda entera sin cortar líneas, y la columna se iba a 550 px. */}
                    {row.deLaPlanilla && onVerPlanilla && (
                        <button
                            type="button"
                            style={{ fontSize: '0.75rem', color: '#15803d', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                            onClick={(e) => { e.stopPropagation(); onVerPlanilla(row); }}
                        >
                            <i className="bi bi-file-earmark-excel me-1" />
                            {planillasAbiertas?.has(row.idTramite) ? 'Ocultar fila de Excel' : 'Fila de Excel'}
                        </button>
                    )}
                    {row.etiqueta && <div style={{ fontSize: '0.78rem', color: '#b45309' }}>{row.etiqueta}</div>}
                    {row.ultimoComentario && (
                        <div style={{ fontSize: '0.75rem', color: '#4b5563' }}>{celdaTextoEnvuelto(row.ultimoComentario, 200, 2)}</div>
                    )}
                </div>
            ),
        },
        // CUIL en columna propia y no debajo del nombre (como en el resto de los listados): acá
        // se lee y se dicta todo el tiempo, y en 0.8rem gris abajo del apellido no se puede.
        {
            key: 'cuil', header: 'CUIL',
            render: (row) => (row.cuilCliente
                ? (
                    <DatoCopiable
                        valor={row.cuilCliente}
                        estilo={{ fontFamily: 'var(--font-family-mono)', fontSize: '0.9rem', color: '#374151', whiteSpace: 'nowrap' }}
                    />
                )
                : <span style={{ color: '#6b7280' }}>—</span>),
        },
        // La carpeta física va debajo del tipo y no en columna propia: con ocho columnas la tabla no
        // entraba en 1366 px sin scroll horizontal.
        {
            key: 'tramite', header: 'Trámite / carpeta',
            render: (row) => (
                <div>
                    <div>{row.titulo || row.tipoTramite || '—'}</div>
                    {row.carpetaNombre && (
                        <div style={{ fontSize: '0.75rem', color: '#4b5563' }}>
                            <i className="bi bi-folder2 me-1" />{row.carpetaNombre}
                        </div>
                    )}
                </div>
            ),
        },
        { key: 'referencia', header: 'Referencia', className: 'text-start', render: (row) => celdaTextoLargo(row.referencia, 120) },
        { key: 'claves', header: 'Claves', className: 'text-start', render: (row) => <CeldaClaves row={row} /> },
        {
            key: 'fechaInicioEstimada', header: 'Fecha de liquidar',
            render: (row) => <CeldaFechaLiquidar row={row} onAsignarMes={row.deLaPlanilla ? onAsignarMes : null} />,
        },
        { key: 'control', header: 'Control', className: 'text-start', render: (row) => <CeldaControl row={row} onGestionar={onGestionar} /> },
    ];
}

/**
 * Texto que se lee COMPLETO: en vez de cortarlo con "…" y dejarlo en el `title`, envuelve en
 * varias líneas dentro de un ancho fijo.
 *
 * Es la contraparte de `celdaTextoLargo`, que recorta. El recorte sirve cuando el dato es
 * identificatorio y alcanza con reconocerlo (el nombre de una referencia), pero no cuando hay que
 * LEERLO: la observación de una carpeta es la nota en rojo que el Excel metía dentro del nombre
 * del cliente, y truncada no sirve para nada.
 *
 * Corta SIEMPRE en los espacios: `overflowWrap: break-word` parte una palabra al medio solo
 * cuando esa palabra sola no entra (un número de expediente larguísimo sin espacios), que es
 * justo lo que se quiere. OJO con `wordBreak: break-word`, que es lo que había antes: equivale a
 * `overflow-wrap: anywhere`, y ese valor SÍ achica el ancho mínimo de la celda, así que en una
 * tabla el navegador aprovecha y parte los nombres al medio ("GONZA / LEZ") aunque haya lugar
 * para bajarlos de línea enteros.
 *
 * @param lineas  máximo de líneas antes de recortar de verdad (con el texto completo en el title).
 *                Evita que una observación de 500 caracteres haga una fila de 20 líneas de alto.
 */
function celdaTextoEnvuelto(valor, ancho = 240, lineas = 4) {
    if (!valor) return <span className="text-muted">—</span>;
    return (
        <span
            title={valor}
            style={{
                display: '-webkit-box',
                WebkitLineClamp: lineas,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                maxWidth: ancho,
                whiteSpace: 'normal',
                wordBreak: 'normal',
                overflowWrap: 'break-word',
                lineHeight: 1.35,
            }}
        >
            {valor}
        </span>
    );
}

/**
 * Semáforo del plazo de carga en Jauregui. Los días los calcula el backend con el plazo de cada
 * carpeta (30 o 60), así que acá solo se pinta: el listado y la ficha no pueden discrepar.
 *
 * Las carpetas de "Carpetas viejas perdidas" llegan con `bloqueSinPlazo`: su plazo ya no significa
 * nada y pintarlas en rojo para siempre sería ruido permanente.
 */
/**
 * "25 d" con espacio DURO entre el número y la unidad.
 *
 * En una columna angosta el espacio normal es un punto de corte válido, así que el navegador
 * bajaba la "d" sola al renglón siguiente y quedaba el número huérfano arriba. Con   el par
 * viaja junto; el resto de la frase ("Demorado hace…") sigue pudiendo cortarse donde haga falta.
 */
function diasConUnidad(dias) {
    return `${dias} d`;
}

function CeldaPlazoJauregui({ row }) {
    if (row.bloqueSinPlazo) {
        return <span style={{ color: '#cbd5e1' }}>—</span>;
    }
    if (!row.fechaPase) {
        return <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Sin fecha de pase</span>;
    }
    const dias = row.diasRestantesCarga;
    const demorado = row.demorado === true;
    const color = demorado ? '#b91c1c' : '#15803d';
    return (
        <div className="text-start" style={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
            {/* Sin ícono: el color ya dice si está en plazo o demorado, y el tilde/triángulo
                sumaban ruido en una tabla donde cada fila ya tiene varios. */}
            <div style={{ color, fontWeight: 600 }}>
                {dias == null
                    ? (demorado ? 'Demorado' : 'En plazo')
                    : dias < 0
                        ? `Demorado hace ${diasConUnidad(Math.abs(dias))}`
                        : dias === 0 ? 'Vence hoy' : `Quedan ${diasConUnidad(dias)}`}
            </div>
            <div style={{ color: '#9ca3af' }}>
                {formatoFecha(row.fechaPase)} · {row.plazoDias} días
            </div>
        </div>
    );
}

/** El llamado al cliente (el segundo plazo), para las carpetas que ya esperan turno. */
function CeldaLlamadoJauregui({ row }) {
    if (!row.fechaLlamado) {
        return <span style={{ color: '#cbd5e1' }}>—</span>;
    }
    const dias = row.diasRestantesLlamado;
    const vencido = dias != null && dias < 0;
    return (
        <div className="text-start" style={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
            <div style={{ color: '#374151' }}>{formatoFecha(row.fechaLlamado)}</div>
            {dias != null && (
                <div style={{ color: vencido ? '#b91c1c' : '#15803d', fontWeight: 600 }}>
                    {dias < 0
                        ? `Fuera de plazo (${diasConUnidad(Math.abs(dias))})`
                        : dias === 0 ? 'Vence hoy' : `Quedan ${diasConUnidad(dias)}`}
                </div>
            )}
        </div>
    );
}

/** El resultado del cálculo. "El titular consultó" es prioridad y va marcado. */
function CeldaResultadoJauregui({ row }) {
    if (!row.resultado) {
        return <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Sin cargar</span>;
    }
    const esPrioridad = row.prioridad === true;
    return (
        <div className="text-start" style={{ fontSize: '0.8rem' }}>
            {/* Sin estrella: la prioridad ya se ve en el ámbar de toda la fila y en la negrita. */}
            <span style={{
                fontWeight: esPrioridad ? 700 : 500,
                color: esPrioridad ? '#92400e' : '#374151',
            }}>
                {row.resultadoEtiqueta}
            </span>
            {row.fechaResultado && (
                <div style={{ color: '#9ca3af' }}>{formatoFecha(row.fechaResultado)}</div>
            )}
        </div>
    );
}

// ── Volver a contactar ──────────────────────────────────────────────────────

/** Días entre una fecha ISO y hoy, contando días de calendario en hora local. */
function diasHasta(fechaISO, hoy = new Date()) {
    if (!fechaISO) return null;
    const fecha = new Date(`${String(fechaISO).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(fecha.getTime())) return null;
    const base = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    return Math.round((fecha - base) / 86400000);
}

/**
 * "hace 2 meses y 5 días": el tiempo desde la consulta, en meses de calendario. Es lo primero que
 * mira el operador para decidir a quién escribirle, y "hace 67 días" no se lee de un vistazo.
 */
export function tiempoDesde(fechaISO, hoy = new Date()) {
    if (!fechaISO) return '—';
    const fecha = new Date(`${String(fechaISO).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(fecha.getTime())) return '—';
    const base = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    if (fecha >= base) return 'Hoy';
    let meses = (base.getFullYear() - fecha.getFullYear()) * 12 + (base.getMonth() - fecha.getMonth());
    if (base.getDate() < fecha.getDate()) meses -= 1;
    const desdeMeses = new Date(fecha.getFullYear(), fecha.getMonth() + meses, fecha.getDate());
    const dias = Math.round((base - desdeMeses) / 86400000);
    const textoDias = dias === 1 ? '1 día' : `${dias} días`;
    if (meses <= 0) return `hace ${textoDias}`;
    const textoMeses = meses === 1 ? '1 mes' : `${meses} meses`;
    return dias > 0 ? `hace ${textoMeses} y ${textoDias}` : `hace ${textoMeses}`;
}

function CeldaTiempoVolverAContactar({ row }) {
    const faltan = diasHasta(row.avisarDesde);
    return (
        <div className="text-center" style={{ fontSize: '0.8rem', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
            <div style={{ fontWeight: 600, color: '#374151' }}>{tiempoDesde(row.fechaConsulta)}</div>
            {row.bloque === 'MAS_ADELANTE' && (
                <div style={{ color: '#6b7280' }}>
                    Avisar desde {formatoFecha(row.avisarDesde)}{faltan != null && faltan > 0 && ` · faltan ${faltan} d`}
                </div>
            )}
        </div>
    );
}

function CeldaAvisoVolverAContactar({ row, onAvisar }) {
    const sinTelefono = !row.telefono;
    return (
        <div className="d-inline-flex flex-column align-items-center gap-1 text-center" style={{ fontSize: '0.8rem', maxWidth: 170 }}>
            {row.fechaUltimoAviso ? (
                <span style={{ color: '#047857', fontWeight: 600 }}>
                    <i className="bi bi-check2-all me-1" />
                    Avisado {new Date(row.fechaUltimoAviso).toLocaleDateString('es-AR')}
                    {row.usuarioAviso && <span style={{ color: '#4b5563', fontWeight: 400 }}> · {row.usuarioAviso}</span>}
                    {row.cantidadAvisos > 1 && <span style={{ color: '#4b5563', fontWeight: 400 }}> ({row.cantidadAvisos} avisos)</span>}
                </span>
            ) : (
                <span style={{ color: '#4b5563' }}>Sin avisar</span>
            )}
            {/* Envuelto en un span: si el botón queda hijo directo del div, la regla global
                "td:has(> div > .btn)" deja la celda sin cortar líneas y ensancha la tabla. */}
            {onAvisar && (
                <span title={sinTelefono ? 'El turno no tiene teléfono cargado' : undefined}>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-success"
                        style={{ padding: '1px 8px', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                        disabled={sinTelefono}
                        onClick={(e) => { e.stopPropagation(); onAvisar(row); }}
                    >
                        <i className="bi bi-whatsapp me-1" />{row.fechaUltimoAviso ? 'Volver a avisar' : 'Avisar'}
                    </button>
                </span>
            )}
        </div>
    );
}

/**
 * Las columnas de "Volver a contactar". Función y no constante por lo mismo que columnasALiquidar:
 * los botones Avisar y Quitar abren modales que solo tiene la página.
 *
 * La columna "Por qué no avanzó" junta el motivo, su detalle, la descripción del turno y la nota
 * del alta: es lo que el operador lee para acordarse de quién es la persona antes de escribirle.
 */
export function columnasVolverAContactar(acciones = {}) {
    const { onAvisar = null, onQuitar = null } = acciones;
    const columnas = [
        {
            key: 'persona', header: 'Persona', className: 'text-start',
            render: (row) => (
                <div className="text-start" style={{ maxWidth: 200, whiteSpace: 'normal', wordBreak: 'normal', overflowWrap: 'break-word' }}>
                    <div style={{ fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>
                        {apellidoNombre(row.apellido, row.nombre) || 'N/A'}
                    </div>
                    {row.referencia && <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Ref: {row.referencia}</div>}
                </div>
            ),
        },
        // CUIL en columna propia y copiable con un click, como en "A liquidar": es el dato que se
        // pega en ANSES para ver cómo está la persona antes de escribirle. La mayoría de estas
        // consultas no son clientes y no lo tienen: entonces va el guion.
        {
            key: 'cuil', header: 'CUIL',
            render: (row) => (row.cuil
                ? (
                    <DatoCopiable
                        valor={row.cuil}
                        estilo={{ fontFamily: 'var(--font-family-mono)', fontSize: '0.9rem', color: '#374151', whiteSpace: 'nowrap' }}
                    />
                )
                : <span style={{ color: '#6b7280' }}>—</span>),
        },
        {
            key: 'telefono', header: 'Teléfono',
            render: (row) => (row.telefono
                ? <DatoCopiable valor={row.telefono} estilo={{ color: '#374151', whiteSpace: 'nowrap' }} />
                : <span style={{ color: '#b91c1c', fontSize: '0.8rem' }}>Sin teléfono</span>),
        },
        {
            key: 'consulta', header: 'Consulta', className: 'text-start',
            render: (row) => (
                <div className="text-start" style={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                    <div style={{ fontWeight: 600, color: '#374151' }}>{formatearTipoTramite(row.tipoTramite)}</div>
                    <div style={{ color: '#4b5563' }}>{formatoFecha(row.fechaConsulta)}</div>
                    {row.operador && <div style={{ color: '#6b7280' }}>Atendió {row.operador}</div>}
                </div>
            ),
        },
        {
            key: 'motivo', header: 'Por qué no avanzó',
            // El bloque va centrado en la celda (como su cabecera) y el texto adentro, a la
            // izquierda: un párrafo centrado no se lee.
            render: (row) => (
                <div className="text-start d-inline-block" style={{ fontSize: '0.8rem', lineHeight: 1.35, maxWidth: 320 }}>
                    <div style={{ fontWeight: 700, color: '#92400e' }}>
                        {row.motivoNoAvance || 'Sin motivo'}
                        {row.resultadoAtencion && row.resultadoAtencion.toLowerCase() !== 'no viable' && (
                            <span style={{ fontWeight: 400, color: '#6b7280' }}> · {row.resultadoAtencion}</span>
                        )}
                    </div>
                    {row.observacionMotivoNoAvance && (
                        <div style={{ color: '#374151' }}>{celdaTextoEnvuelto(row.observacionMotivoNoAvance, 300, 3)}</div>
                    )}
                    {row.descripcionTurno && (
                        <div style={{ color: '#6b7280', fontStyle: 'italic' }}>{celdaTextoEnvuelto(row.descripcionTurno, 300, 2)}</div>
                    )}
                    {row.nota && (
                        <div style={{ color: '#1e40af' }}>
                            <i className="bi bi-sticky me-1" />{celdaTextoEnvuelto(row.nota, 280, 2)}
                        </div>
                    )}
                </div>
            ),
        },
        { key: 'tiempo', header: 'Tiempo desde la consulta', render: (row) => <CeldaTiempoVolverAContactar row={row} /> },
        { key: 'aviso', header: 'Aviso', render: (row) => <CeldaAvisoVolverAContactar row={row} onAvisar={onAvisar} /> },
    ];
    if (onQuitar) {
        columnas.push({
            key: 'quitar', header: '',
            render: (row) => (
                <span>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        style={{ padding: '1px 8px', fontSize: '0.75rem' }}
                        title="Quitar del listado"
                        aria-label={`Quitar a ${apellidoNombre(row.apellido, row.nombre)} del listado`}
                        onClick={(e) => { e.stopPropagation(); onQuitar(row); }}
                    >
                        <i className="bi bi-x-lg" />
                    </button>
                </span>
            ),
        });
    }
    return columnas;
}

// ── Deudores ────────────────────────────────────────────────────────────────

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** "2022-08-01" → "ago 2022": el bloque de mes de la planilla NUEVO DEUDORES (V273). */
export function formatoMesPlanilla(iso) {
    if (!iso) return 'sin mes';
    const [anio, mes] = iso.split('-');
    return `${MESES_CORTOS[Number(mes) - 1]} ${anio}`;
}

/** "hace 3 días" / "hoy" / "ayer": cuánto pasó desde una fecha (o fecha y hora), en minúscula para ir en una frase. */
function haceCuanto(fechaISO) {
    const texto = tiempoDesde(fechaISO);
    if (texto === 'Hoy') return 'hoy';
    if (texto === 'hace 1 día') return 'ayer';
    return texto;
}

/**
 * Cómo está la deuda, y nada más: con cuota vencida, hace cuánto (en rojo) y desde qué fecha; al
 * día, la fecha del próximo vencimiento. Lo de contactarlo va en la columna de al lado.
 */
function CeldaVencimientoDeudor({ row }) {
    if (row.cuotaVencida) {
        return (
            <div className="d-inline-flex flex-column align-items-center text-center" style={{ fontSize: '0.8rem', lineHeight: 1.35 }}>
                <span style={{ color: '#b91c1c', fontWeight: 700 }}>Vencida {haceCuanto(row.vencidaDesde)}</span>
                <span style={{ color: '#6b7280' }}>desde el {formatoFecha(row.vencidaDesde)}</span>
            </div>
        );
    }
    return <span style={{ color: '#374151', fontWeight: 600, fontSize: '0.85rem' }}>{formatoFecha(row.proximoVencimiento)}</span>;
}

/**
 * Qué se hizo para contactarlo: el último recordatorio (hace cuánto y quién: el automático o la
 * persona que lo mandó) y, con las cuotas al día, cuándo sale el próximo automático. Es lo que se
 * mira antes de escribirle de nuevo, para no llenarlo de mensajes.
 *
 * El botón "Recordar" aparece solo con cuota vencida: antes del vencimiento ya avisa el automático.
 */
function CeldaRecordatorioDeudor({ row, onRecordar }) {
    let ultimo;
    if (row.ultimoRecordatorio) {
        const quien = row.ultimoRecordatorioAutomatico ? 'automático' : (row.ultimoRecordatorioUsuario || 'a mano');
        const cuando = haceCuanto(row.ultimoRecordatorio);
        ultimo = (
            <span style={{ color: '#374151' }}>
                <span style={{ fontWeight: 600 }}>{cuando.charAt(0).toUpperCase() + cuando.slice(1)}</span> · {quien}
            </span>
        );
    } else {
        ultimo = <span style={{ color: '#6b7280' }}>{row.telefono ? 'Sin recordatorios' : 'Sin teléfono'}</span>;
    }
    return (
        <div className="d-inline-flex flex-column align-items-center gap-1 text-center" style={{ fontSize: '0.8rem', lineHeight: 1.35 }}>
            {ultimo}
            {!row.cuotaVencida && row.proximoRecordatorio && (
                <span style={{ color: '#6b7280' }}>Automático: sale el {formatoFecha(row.proximoRecordatorio)}</span>
            )}
            {/* Envuelto en un span: si el botón queda hijo directo del div, la regla global
                "td:has(> div > .btn)" deja la celda sin cortar líneas y ensancha la tabla. */}
            {row.cuotaVencida && onRecordar && (
                <span title={row.telefono ? undefined : 'El cliente no tiene teléfono cargado'}>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        style={{ padding: '1px 8px', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                        disabled={!row.telefono}
                        onClick={(e) => { e.stopPropagation(); onRecordar(row); }}
                    >
                        <i className="bi bi-whatsapp me-1" />Recordar
                    </button>
                </span>
            )}
        </div>
    );
}

/**
 * Las columnas de Deudores. Función y no constante, como columnasALiquidar: el botón "Recordar"
 * abre un modal que solo tiene la página.
 */
export function columnasDeudores(acciones = {}) {
    const { onRecordar = null, onCambiarRecordatorio = null } = acciones;
    return [
        columnaCliente, columnaTramite,
        columnaTelefono,
        { key: 'referencia', header: 'Referencia', render: (row) => row.referencia || '—' },
        {
            key: 'saldo', header: 'Adeuda',
            render: (row) => <span style={{ fontWeight: 600, color: row.cuotaVencida ? '#b91c1c' : '#374151' }}>{esPagoTodo(row) ? PAGO_TODO : formatoMoneda(row.saldo)}</span>,
        },
        {
            key: 'plan', header: 'Plan',
            render: (row) => row.planDescripcion
                || (row.migrado && row.cuotasTotales === 0 ? <span style={{ color: '#b45309', fontWeight: 600 }}>Sin plan</span> : '—'),
        },
        { key: 'cuotas', header: 'Cuotas', render: (row) => `${row.cuotasPagadas}/${row.cuotasTotales}` },
        { key: 'vencimiento', header: 'Vencimiento', render: (row) => <CeldaVencimientoDeudor row={row} /> },
        {
            key: 'recordatorio', header: 'Recordatorio',
            render: (row) => (
                <>
                    <CeldaRecordatorioDeudor row={row} onRecordar={onRecordar} />
                    {row.migrado && (
                        <div className="text-center mt-1" style={{ fontSize: '0.75rem' }}>
                            <span title={`De la planilla${row.mesPlanilla ? ` (bloque ${formatoMesPlanilla(row.mesPlanilla)})` : ''}`}
                                  style={{ color: row.recordatorioActivo ? '#166534' : '#6b7280' }}>
                                <i className={`bi ${row.recordatorioActivo ? 'bi-bell-fill' : 'bi-bell-slash'} me-1`} />
                                {row.recordatorioActivo ? 'Automático activo' : 'Automático apagado'}
                            </span>
                            {onCambiarRecordatorio && (
                                <button type="button" className="btn btn-link btn-sm p-0 ms-1" style={{ fontSize: '0.75rem' }}
                                        onClick={(e) => { e.stopPropagation(); onCambiarRecordatorio(row, !row.recordatorioActivo); }}>
                                    {row.recordatorioActivo ? 'apagar' : 'activar'}
                                </button>
                            )}
                        </div>
                    )}
                </>
            ),
        },
        columnaUltimaGestion,
    ];
}

export const LISTADOS = {
    aCobrar: {
        clave: 'aCobrar',
        titulo: 'Favorables',
        subtitulo: 'Salió favorable, todavía no cobró el primer haber',
        ruta: '/listados/a-cobrar',
        icono: 'bi bi-cash-stack',
        permiso: 'listados.ver',
        fetchPage: (page, size, texto) => getCobros('A_COBRAR', texto, page, size, 'fechaFavorable,desc'),
        // Las columnas que pidió el estudio (oct-2026), en el orden de la solapa FAVORABLES de la
        // planilla: nombre, CUIL, trámite, referencia, estado y fecha ("FAVORABLE 19/08/2026") y
        // pacto. Teléfono y última gestión quedan al final: es un listado de cobranza.
        columnas: [
            {
                key: 'cliente', header: 'Cliente', className: 'text-start',
                render: (row) => (
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>
                        {[row.apellido, row.nombre].filter(Boolean).join(', ') || 'N/A'}
                    </span>
                ),
            },
            {
                key: 'cuil', header: 'CUIL',
                render: (row) => (row.cuil ? <DatoCopiable valor={row.cuil} estilo={{ color: '#374151' }} /> : '—'),
            },
            columnaTramite,
            { key: 'referencia', header: 'Referencia', render: (row) => celdaTextoLargo(row.referencia, 160) },
            {
                key: 'estado', header: 'Estado',
                render: (row) => (
                    <span style={{
                        display: 'inline-block', background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
                        borderRadius: 12, padding: '1px 10px', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap',
                    }}>
                        Favorable{row.fechaFavorable ? ` ${formatoFecha(row.fechaFavorable)}` : ''}
                    </span>
                ),
            },
            columnaPacto,
            columnaTelefono,
            columnaUltimaGestion,
        ],
    },
    altas: {
        clave: 'altas',
        titulo: 'Altas',
        subtitulo: 'Ya cobra el beneficio, se lo cita al estudio a pagar',
        ruta: '/listados/altas',
        icono: 'bi bi-check2-circle',
        permiso: 'listados.ver',
        // Sin los archivados (V269): pasar a Archivo es justamente sacarlo de Altas.
        fetchPage: (page, size, texto) => getCobros('ALTA', texto, page, size, 'fechaAlta,desc', false, { vista: 'ALTAS' }),
        columnas: [
            columnaCliente, columnaTramite,
            columnaTelefono, columnaBanco,
            { key: 'fechaAlta', header: 'Fecha de alta', render: (row) => formatoFecha(row.fechaAlta) },
            columnaObservaciones,
        ],
    },
    deudores: {
        clave: 'deudores',
        titulo: 'Deudores',
        subtitulo: 'Quedó saldo financiado en cuotas',
        ruta: '/listados/deudores',
        icono: 'bi bi-exclamation-triangle',
        permiso: 'listados.ver',
        // Vista DEUDORES: en cuotas, más los deudores de la planilla sin plan ("NO VINO AL COBRO", V273).
        fetchPage: (page, size, texto, filtros = {}) => getCobros(null, texto, page, size, 'creadoEn,desc', undefined,
            { vista: 'DEUDORES', ...filtros }),
        // Fila en rojo si tiene alguna cuota vencida sin pagar — la "nota en rojo" del Excel.
        rowClassName: (row) => row.cuotaVencida ? { background: '#fef2f2' } : {},
        // El botón "Recordar" abre un modal que solo tiene la página: ver columnasDeudores().
        columnas: columnasDeudores(),
    },
    // Planilla histórica (V253): las solapas del Excel que no son trabajo en curso. No son clientes
    // del sistema; las páginas arman sus propias columnas (ListadoPlanillaHistorica.js).
    archivo: {
        clave: 'archivo',
        titulo: 'Archivo',
        subtitulo: 'Clientes que ya cobraron (planilla y cobros archivados), con el seguimiento del reajuste',
        ruta: '/listados/archivo',
        icono: 'bi bi-archive',
        permiso: 'listados.ver',
        fetchPage: (page, size, texto) => listarPlanilla('ARCHIVO', page, size, texto),
        columnas: [],
    },
    carpetasEliminadas: {
        clave: 'carpetasEliminadas',
        titulo: 'Carpetas eliminadas',
        subtitulo: 'Clientes y trámites que se dejaron (de la planilla), con el motivo',
        ruta: '/listados/carpetas-eliminadas',
        icono: 'bi bi-folder-x',
        permiso: 'listados.ver',
        fetchPage: (page, size, texto) => listarPlanilla('CARPETA_ELIMINADA', page, size, texto),
        columnas: [],
    },
    jauregui: {
        clave: 'jauregui',
        titulo: 'Para cargar Jauregui',
        subtitulo: 'Carpetas pasadas a cargar el cálculo (tramo previo a liquidar)',
        ruta: '/listados/cargar-jauregui',
        icono: 'bi bi-calculator',
        permiso: 'listados.ver',
        // Sin filtros: ListadoCargarJauregui.js arma su propio fetchPage con los que elija el
        // usuario (bloque, quién carga, estado del plazo, tipo).
        fetchPage: (page, size, texto) => getCarpetasJauregui({}, page, size, texto),
        // Fila en ámbar si el titular vino a preguntar: es la "prioridad" que pidió el estudio, y
        // el mismo mecanismo con el que Deudores pinta las cuotas vencidas.
        rowClassName: (row) => (row.prioridad ? { background: '#fffbeb' } : {}),
        // Mismas columnas y mismo orden que la pestaña PARA CARGAR JAUREGUI de la planilla.
        columnas: [
            {
                key: 'cliente', header: 'Cliente', className: 'text-start',
                render: (row) => (
                    // whiteSpace normal + maxWidth: un nombre largo baja de línea en vez de
                    // cortarse. La celda la agranda la fila, no empuja la tabla a lo ancho.
                    // overflowWrap y no wordBreak: así corta entre palabras y no al medio de una
                    // (ver celdaTextoEnvuelto, donde está explicada la diferencia).
                    <div className="text-start" style={{ maxWidth: 200, whiteSpace: 'normal', wordBreak: 'normal', overflowWrap: 'break-word' }}>
                        <div style={{ fontWeight: 500, color: '#374151', lineHeight: 1.3 }}>
                            {apellidoNombre(row.apellidoCliente, row.nombreCliente) || 'N/A'}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#9ca3af' }}>{row.cuilCliente}</div>
                        {row.etiqueta && (
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>{row.etiqueta}</div>
                        )}
                    </div>
                ),
            },
            {
                key: 'tramite', header: 'Trámite', className: 'text-start',
                render: (row) => (
                    <div className="text-start" style={{ fontSize: '0.85rem' }}>
                        <div style={{ color: '#374151' }}>{row.titulo || row.tipoTramite || '—'}</div>
                        {row.detalleTramite && (
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', textTransform: 'capitalize' }}>
                                {row.detalleTramite.toLowerCase()}
                            </div>
                        )}
                    </div>
                ),
            },
            { key: 'telefono', header: 'Teléfono', render: (row) => row.telefono || '—' },
            { key: 'referencia', header: 'Referencia', className: 'text-start', render: (row) => celdaTextoEnvuelto(row.referencia, 150, 3) },
            { key: 'plazo', header: 'Plazo de carga', className: 'text-start', render: (row) => <CeldaPlazoJauregui row={row} /> },
            { key: 'usuarioCarga', header: '¿Quién carga?', render: (row) => row.usuarioCarga || <span style={{ color: '#cbd5e1' }}>Sin asignar</span> },
            { key: 'resultado', header: 'Resultado', className: 'text-start', render: (row) => <CeldaResultadoJauregui row={row} /> },
            { key: 'observacion', header: 'Observación', className: 'text-start', render: (row) => celdaTextoEnvuelto(row.observacion, 260, 5) },
            { key: 'llamado', header: 'Llamado / turno', className: 'text-start', render: (row) => <CeldaLlamadoJauregui row={row} /> },
            { key: 'claves', header: 'Claves', className: 'text-start', render: (row) => <CeldaClaves row={row} /> },
        ],
    },
    aLiquidar: {
        clave: 'aLiquidar',
        titulo: 'A liquidar',
        subtitulo: 'Carpetas esperando para iniciar el trámite',
        ruta: '/listados/a-liquidar',
        icono: 'bi bi-hourglass-split',
        permiso: 'listados.ver',
        // Sin filtro de mes: ListadoALiquidar.js arma su propio fetchPage envolviendo
        // getTramitesParaLiquidar con el mes elegido (ver el estado `mes` de esa página).
        fetchPage: (page, size, texto) => getTramitesParaLiquidar('', page, size, texto),
        // Las columnas se arman con una función porque la última necesita un callback (abrir el
        // modal de control), y eso solo lo tiene la página. Ver columnasALiquidar().
        columnas: columnasALiquidar(),
    },
    volverAContactar: {
        clave: 'volverAContactar',
        titulo: 'Volver a contactar',
        subtitulo: 'Consultas que no avanzaron y se pueden retomar',
        ruta: '/listados/volver-a-contactar',
        icono: 'bi bi-telephone-outbound',
        permiso: 'listados.ver',
        // Sin filtros: ListadoVolverAContactar.js arma su propio fetchPage con motivo y tipo.
        fetchPage: (page, size, texto) => listarVolverAContactar({}, page, size, texto),
        // Las columnas con botones (Avisar, Quitar) las arma la página: ver columnasVolverAContactar().
        columnas: columnasVolverAContactar(),
    },
    paraAprobar: {
        clave: 'paraAprobar',
        titulo: 'Para aprobar',
        subtitulo: 'Presentado en el organismo, esperando resolución',
        ruta: '/listados/para-aprobar',
        icono: 'bi bi-hourglass-bottom',
        permiso: 'listados.ver',
        // Sin filtro de grupo: ListadoParaAprobar.js arma su propio fetchPage con el grupo elegido.
        fetchPage: (page, size, texto) => getTramitesParaAprobar({}, page, size, texto),
        // Mismas columnas y mismo orden que la solapa PARA APROBAR de la planilla del estudio.
        columnas: [
            {
                key: 'cliente', header: 'Cliente', className: 'text-start',
                render: (row) => (
                    <div className="text-start">
                        <div style={{ fontWeight: 500, color: '#374151' }}>
                            {apellidoNombre(row.apellidoCliente, row.nombreCliente) || 'N/A'}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#4b5563' }}>{row.cuilCliente}</div>
                        {row.etiqueta && <div style={{ fontSize: '0.78rem', color: '#b45309' }}>{row.etiqueta}</div>}
                    </div>
                ),
            },
            {
                key: 'tramite', header: 'Trámite', className: 'text-start',
                render: (row) => (
                    <div className="text-start">
                        <div>{row.titulo || row.tipoTramite || '—'}</div>
                        {row.detalleTramite && <div style={{ fontSize: '0.78rem', color: '#4b5563' }}>{row.detalleTramite}</div>}
                    </div>
                ),
            },
            {
                key: 'estado', header: 'Estado', className: 'text-start',
                render: (row) => (
                    <div className="text-start">
                        <div style={{ fontWeight: 500 }}>{row.estadoActual || '—'}</div>
                        {row.ultimoComentario && (
                            <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{celdaTextoLargo(row.ultimoComentario, 260)}</div>
                        )}
                    </div>
                ),
            },
            { key: 'referencia', header: 'Referencia', render: (row) => row.referencia || '—' },
            { key: 'telefono', header: 'Teléfono', render: (row) => celdaTextoLargo(row.telefono, 150) },
            { key: 'nroExpediente', header: 'N° expediente', render: (row) => row.nroExpediente || celdaSinConectar() },
            { key: 'fechaIngresoAnses', header: 'Ingreso a ANSES', render: (row) => row.fechaIngresoAnses ? formatoFecha(row.fechaIngresoAnses) : celdaSinConectar() },
            // "Cuándo generar amparo" se eliminó en V239: nunca estuvo conectada y el plazo no es
            // fijo (depende del tipo de trámite y a veces se corrige a mano). Calcularlo entra en
            // otra presupuestación.
            //
            // Las dos de abajo SÍ están conectadas desde V239: salen del trámite de Amparo por mora
            // vinculado a este trámite de fondo. Un guion acá significa "no tiene amparo", que es la
            // ausencia real — ya no es un dato sin conectar, así que no va celdaSinConectar().
            { key: 'amparoPorMora', header: 'Amparo por mora', render: (row) => row.amparoPorMora || '—' },
            { key: 'fechaProntoDespacho', header: 'Pronto despacho', render: (row) => row.fechaProntoDespacho ? formatoFecha(row.fechaProntoDespacho) : '—' },
            { key: 'estudiosMedicos', header: 'Estudios médicos', render: (row) => row.estudiosMedicos || celdaSinConectar() },
            { key: 'estadoJuntaPericia', header: 'Junta y pericia', render: (row) => row.estadoJuntaPericia ? celdaTextoLargo(row.estadoJuntaPericia, 220) : celdaSinConectar() },
            { key: 'claveSs', header: 'CSS', render: (row) => row.claveSs || '—' },
            { key: 'claveFiscal', header: 'Clave fiscal', render: (row) => row.claveFiscal || '—' },
            { key: 'claveCidi', header: 'CIDI', render: (row) => row.claveCidi || '—' },
            { key: 'miArgentina', header: 'Mi Argentina', render: (row) => row.miArgentina || '—' },
        ],
    },
};

/**
 * El orden en que se muestran los listados: el del circuito real del estudio, no el alfabético
 * ni el orden en que se fueron programando.
 *
 *   a liquidar → (turno ANSES) → para aprobar → favorables → altas → deudores
 *
 * Un trámite los recorre en ese orden, así que el menú y la barra de accesos se leen como el
 * recorrido. Está declarado aparte de LISTADOS para poder agregar un listado nuevo sin tener que
 * insertarlo en el lugar justo del objeto.
 */
// Primero los 5 tramos del ciclo del trámite; después los listados auxiliares (Jauregui de
// reajuste y los que se agreguen), separados en el menú ("OTROS LISTADOS") y en la barra de accesos.
const ORDEN_CIRCUITO = ['aLiquidar', 'paraAprobar', 'aCobrar', 'altas', 'deudores'];
const ORDEN_OTROS = ['jauregui', 'volverAContactar', 'archivo', 'carpetasEliminadas'];

export const LISTADOS_CICLO = ORDEN_CIRCUITO.map((clave) => LISTADOS[clave]).filter(Boolean);

export const LISTADOS_OTROS = ORDEN_OTROS.map((clave) => LISTADOS[clave]).filter(Boolean)
    // Red de seguridad: si alguien agrega un listado a LISTADOS y se olvida de ubicarlo, aparece
    // igual (entre los otros) en vez de desaparecer del menú sin que nadie se entere.
    .concat(Object.values(LISTADOS).filter((l) => !ORDEN_CIRCUITO.includes(l.clave) && !ORDEN_OTROS.includes(l.clave)));

export const LISTADOS_MENU = [...LISTADOS_CICLO, ...LISTADOS_OTROS];
